import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Search, X, User, Phone, MapPin, CreditCard, Loader2 } from 'lucide-react';
import api from '../lib/api';

const CustomerSearch = ({ customers, value, onChange, placeholder, colorScheme = 'blue', testId, tipo }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [serverResults, setServerResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  const selected = customers.find(c => c.id === value);

  // Server-side search when typing 2+ chars
  const searchServer = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setServerResults([]);
      return;
    }
    setSearching(true);
    try {
      const params = { search: q, limit: 30 };
      if (tipo) params.tipo = tipo;
      const res = await api.get('/customers', { params });
      setServerResults(res.data.data || []);
    } catch { /* ignore */ }
    finally { setSearching(false); }
  }, [tipo]);

  // Combine local customers + server results (dedupe)
  const allCustomers = React.useMemo(() => {
    const map = new Map();
    customers.forEach(c => map.set(c.id, c));
    serverResults.forEach(c => map.set(c.id, c));
    return Array.from(map.values());
  }, [customers, serverResults]);

  const filtered = search.trim()
    ? allCustomers.filter(c => {
        const q = search.toLowerCase();
        return (
          (c.nombre_completo || '').toLowerCase().includes(q) ||
          (c.telefono || '').includes(q) ||
          (c.cedula_pasaporte || '').toLowerCase().includes(q) ||
          (c.direccion_completa || '').toLowerCase().includes(q) ||
          (c.ciudad || '').toLowerCase().includes(q)
        );
      }).slice(0, 50)
    : allCustomers.slice(0, 50);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setHighlightIdx(0); }, [search]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const el = listRef.current.children[highlightIdx];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx, isOpen]);

  const handleSearchChange = (val) => {
    setSearch(val);
    setIsOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchServer(val), 300);
  };

  const handleSelect = (customer) => {
    onChange(customer.id);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => { onChange(''); setSearch(''); setIsOpen(false); };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(prev => Math.min(prev + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter' && filtered[highlightIdx]) { e.preventDefault(); handleSelect(filtered[highlightIdx]); }
    else if (e.key === 'Escape') setIsOpen(false);
  };

  const borderColor = colorScheme === 'green' ? 'border-green-200 focus-within:border-green-500' : 'border-blue-200 focus-within:border-blue-500';
  const accentBg = colorScheme === 'green' ? 'bg-green-50' : 'bg-blue-50';
  const accentText = colorScheme === 'green' ? 'text-green-700' : 'text-blue-700';

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {selected && !isOpen ? (
        <div className={`flex items-center justify-between border-2 ${borderColor} rounded-lg px-3 py-2.5 ${accentBg} cursor-pointer`}
          onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        >
          <div className="flex-1 min-w-0">
            <p className={`font-bold text-base ${accentText} truncate`}>{selected.nombre_completo}</p>
            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
              {selected.telefono && <span className="flex items-center gap-0.5"><Phone className="h-3 w-3" />{selected.telefono}</span>}
              {selected.cedula_pasaporte && <span className="flex items-center gap-0.5"><CreditCard className="h-3 w-3" />{selected.cedula_pasaporte}</span>}
              {selected.ciudad && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{selected.ciudad}</span>}
            </div>
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); handleClear(); }} className="ml-2 p-1 rounded-full hover:bg-gray-200 text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className={`relative border-2 ${borderColor} rounded-lg transition-colors`}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || 'Buscar por nombre, telefono, cedula...'}
            className="h-12 text-base pl-9 pr-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            data-testid={`${testId}-input`}
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />}
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto" ref={listRef}>
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm">
              <User className="h-8 w-8 mx-auto mb-1 opacity-40" />
              {searching ? 'Buscando...' : 'No se encontraron resultados'}
            </div>
          ) : (
            filtered.map((c, idx) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelect(c)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors border-b border-gray-50 last:border-0
                  ${idx === highlightIdx ? `${accentBg}` : 'hover:bg-gray-50'}
                  ${c.id === value ? 'ring-2 ring-inset ring-blue-300' : ''}`}
                data-testid={`${testId}-option-${c.id}`}
              >
                <div className={`mt-0.5 p-1.5 rounded-full ${accentBg} flex-shrink-0`}>
                  <User className={`h-4 w-4 ${accentText}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base text-gray-900 truncate">{c.nombre_completo}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                    {c.telefono && <span className="flex items-center gap-0.5"><Phone className="h-3 w-3" />{c.telefono}</span>}
                    {c.cedula_pasaporte && <span className="flex items-center gap-0.5"><CreditCard className="h-3 w-3" />{c.cedula_pasaporte}</span>}
                    {c.ciudad && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{c.ciudad}{c.provincia ? `, ${c.provincia}` : ''}</span>}
                  </div>
                  {c.direccion_completa && (
                    <p className="text-xs text-gray-400 truncate mt-0.5">{c.direccion_completa}{c.apartamento ? `, Apt: ${c.apartamento}` : ''}</p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerSearch;
